import { BellIcon, CircleAlertIcon, CircleDotIcon, Loader2Icon } from "lucide-react";
import type { AgentStatus } from "~/lib/taskThreadLink";
import { cn } from "~/lib/utils";

const LABEL: Record<Exclude<AgentStatus, "none">, string> = {
  running: "Running",
  idle: "Idle",
  error: "Error",
  "pending-approval": "Needs approval",
  "pending-input": "Needs input",
};

/**
 * Live agent/thread status for a task. Color is reserved for active/attention
 * states; idle stays muted. Optional onClick opens the executing thread.
 */
export function AgentStatusBadge({
  agentStatus,
  agentProvider,
  onClick,
  className,
}: {
  readonly agentStatus: AgentStatus;
  readonly agentProvider: string | null;
  readonly onClick?: (() => void) | undefined;
  readonly className?: string | undefined;
}) {
  if (agentStatus === "none") return null;

  const Icon =
    agentStatus === "running"
      ? Loader2Icon
      : agentStatus === "error"
        ? CircleAlertIcon
        : agentStatus.startsWith("pending")
          ? BellIcon
          : CircleDotIcon;
  // `--info` / `--destructive` / `--warning` rather than sky / rose / amber, which
  // were the same three meanings at slightly different hues from the tokens the
  // rest of the app uses for them.
  const tone =
    agentStatus === "running"
      ? "text-info-foreground"
      : agentStatus === "error"
        ? "text-destructive-foreground"
        : agentStatus.startsWith("pending")
          ? "text-warning-foreground"
          : "text-muted-foreground";
  const label = `${agentProvider ? `${agentProvider} · ` : ""}${LABEL[agentStatus]}`;
  const classes = cn(
    "inline-flex items-center gap-1 text-[11px]",
    tone,
    onClick && "hover:underline",
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={classes}
      >
        <Icon className={cn("size-3", agentStatus === "running" && "animate-spin")} aria-hidden />
        {label}
      </button>
    );
  }
  return (
    <span className={classes}>
      <Icon className={cn("size-3", agentStatus === "running" && "animate-spin")} aria-hidden />
      {label}
    </span>
  );
}
